import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type { Dayjs } from 'dayjs';
import { atom, useAtom } from 'jotai';
import { orderBy } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useEffect } from 'react';

import useHandleError from '@/hooks/useHandleError';
import { axiosClient } from '@/services/BaseApi.service';

import { selectedOrganizationAtom } from '../Organizations';
import { CREATE_ANNOUNCEMENT_DIALOG_FORM_DEFAULT_VALUES } from './AnnouncementDialog/AnnouncementDialog.constants';
import type {
  Announcement,
  AnnouncementDTO,
  AttachmentFile,
  NewAttachmentType
} from './Announcements.types';
import { mapAnnouncements } from './Announcements.utils';

export const isModalOpenAtom = atom<boolean>(false);
export const modalTypeAtom = atom<string>('create');
export const selectedAnnouncementAtom = atom<Announcement>(
  CREATE_ANNOUNCEMENT_DIALOG_FORM_DEFAULT_VALUES
);

export const announcementsAtom = atom<Announcement[]>([]);

let prevAnnouncements: AnnouncementDTO[] = [];
export const useFetchAnnouncements = () => {
  const [, setAnnouncements] = useAtom(announcementsAtom);
  const handleError = useHandleError();

  const { data, isError, error, isFetching, refetch, isSuccess } = useQuery<
    AnnouncementDTO[]
  >({
    queryKey: ['announcements/'],
    staleTime: 60 * 1000 // 1 minute
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch announcements.');
    }
    if (data && data !== prevAnnouncements) {
      setAnnouncements(mapAnnouncements(data));
      prevAnnouncements = data;
    }
  }, [isError, isSuccess, data]);

  return {
    isFetching,
    refetch
  };
};

export const draftAnnouncementsAtom = atom((get) => {
  const announcements = get(announcementsAtom);

  return announcements.filter(
    (draftAnnouncements: Announcement) => !draftAnnouncements.is_published
  );
});

export const publishedAnnouncementsAtom = atom((get) => {
  const selectedOrganization = get(selectedOrganizationAtom);
  let announcements = get(announcementsAtom);

  if (selectedOrganization.label !== 'all') {
    announcements = announcements.filter((announcement) =>
      announcement.org_list?.includes(selectedOrganization.label)
    );
  }

  return announcements.filter(
    (publishedAnnouncements: Announcement) =>
      publishedAnnouncements.published_at !== null
  );
});

export const recentAnnouncementsAtom = atom((get) => {
  const publishedAnnouncements = get(publishedAnnouncementsAtom);
  return orderBy(publishedAnnouncements, 'published_at', 'desc').slice(0, 10);
});

export const usePublishAnnouncement = () => {
  const handleError = useHandleError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (publishedAnnouncement: {
      [key: string]: string | boolean | Dayjs | null;
    }) => {
      const { announcement_id, revert } = publishedAnnouncement;
      return axiosClient.get(
        `/announcements/${revert ? 'unpublish' : 'publish'}/${announcement_id}`
      );
    },
    onError: (error: AxiosError<{ detail: string }>, variables) => {
      handleError(
        error,
        error.response?.data?.detail ||
          `Failure to ${variables.revert ? 'un' : ''}publish announcement.`
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['announcements/'] });
      enqueueSnackbar(
        `Announcement ${variables.revert ? 'Unpublished' : 'Published'}`,
        {
          variant: 'success'
        }
      );
    }
  });
};

export const useDeleteAnnouncement = () => {
  const handleError = useHandleError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (announcementId: string) => {
      return axiosClient.delete(`/announcements/${announcementId}`);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to delete announcement.');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements/'] });
      enqueueSnackbar(`Announcement Deleted`, {
        variant: 'success'
      });
    }
  });
};

export const useUpdateAnnouncement = (announcementId: string) => {
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (newAnnouncement: Announcement) => {
      return axiosClient.put(
        `/announcements/${announcementId}`,
        newAnnouncement
      );
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update announcement.');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements/'] });
      enqueueSnackbar(`Announcement Updated`, {
        variant: 'success'
      });
    }
  });
};

export const useCreateAnnouncement = () => {
  const handleError = useHandleError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newAnnouncement: Announcement) => {
      return axiosClient.post(`/announcements/`, newAnnouncement);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to create announcement.');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements/'] });
      enqueueSnackbar(`Announcement Created`, {
        variant: 'success'
      });
    }
  });
};

export const useAddAnnouncementAttachment = () => {
  const handleError = useHandleError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newAttachmentData: NewAttachmentType) => {
      const newAttachment = newAttachmentData.attachments;
      const announcementId = newAttachmentData.id;

      const formData = new FormData();
      newAttachment.forEach((file: AttachmentFile) => {
        if (file instanceof File) {
          formData.append('attachments', file);
        }
      });
      return axiosClient.post(
        `/announcements/${announcementId}/attachments`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Accept: 'application/json'
          }
        }
      );
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to add announcement attachment.');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements/'] });
    }
  });
};

export const useDeleteAnnouncementAttachment = () => {
  const handleError = useHandleError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deleteParams: { [key: string]: string | number }) => {
      return axiosClient.delete(
        `/announcements/${deleteParams.announcementId}/attachments/${deleteParams.key}`
      );
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to delete announcement attachment.');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements/'] });
    }
  });
};
